import { useEffect } from "react";
import { Link, Outlet, NavLink } from "react-router-dom";
import { useGlobalState } from "@/helpers/useGlobalState";
import { getGlobalContentitem } from "@/helpers/contentHelpers";
import type { ImageProps } from "@/types/imageProps";
import { withLocale } from "@/helpers/withLocale";
import MalencoNaw from "@/components/MalencoNaw";
import { getMsalInstance } from "@/auth/msalInstance";
import { logout } from "@/auth/auth";
import "@/css/admin.css";

type ManagerLayoutProps = { titleCode?: string };
export default function ManagerLayout({ titleCode }: ManagerLayoutProps) {
    const { globalState } = useGlobalState();

    const pageTitle = titleCode ? getGlobalContentitem(titleCode, globalState)?.title : "Manager";

    const logo: ImageProps = {
        url: "Malenco_Academy_schild.png",
        alt: getGlobalContentitem("MalencoLogoImg", globalState).title,
        title: getGlobalContentitem("MalencoLogoImg", globalState).content,
    };

    useEffect(() => {
        const title = pageTitle ? `${pageTitle} | Malenco Academy Manager` : "Malenco Academy Manager";
        document.title = title;
    }, [pageTitle]);

    const managerMenuItems = [
        { code: "AcademyDashboard", to: "/manager", fallback: "Dashboard" },
        { code: "AcademyStatistieken", to: "/manager/statistics", fallback: "Statistieken" },
        { code: "AcademyOnboarding", to: "/manager/onboarding", fallback: "Onboarding" },
        { code: "AcademyProgrammas", to: "/manager/programs", fallback: "Programma's" },
        { code: "AcademyGroepen", to: "/manager/groups", fallback: "Groepen" },
        { code: "AcademyBackToAcademy", to: "/", fallback: "← Terug naar Academy" },
    ];

    const navLinkClass = ({ isActive }: { isActive: boolean }) =>
        "AdminNavLink" + (isActive ? " AdminNavLink--active" : "");

    async function handleLogout() {
        const instance = getMsalInstance();
        await logout(instance);
    }

    function getMenuLabel(code: string, fallback: string) {
        return getGlobalContentitem(code, globalState)?.title || fallback;
    }

    function getMenuTooltip(code: string) {
        return getGlobalContentitem(code, globalState)?.content || "";
    }

    return (
        <div className="container">
            <div className="left"></div>

            <div className="center">
                {/* Topbar */}
                <div className="AdminBarSection">
                    <div className="AdminBarLogoSection">
                        <Link to={withLocale("/", globalState.Language)} title="Naar de Academy">
                            <img src={"../media/img/" + logo.url} alt={logo.alt} title={logo.title} />
                        </Link>
                    </div>

                    <div className="AdminBarNameSection">Malenco Academy</div>

                    <div className="AdminBarTitleSection">
                        <h1>{pageTitle}</h1>
                    </div>

                    <div className="AdminBarRightSection">{/* Placeholder for user menu */}</div>
                </div>

                {/* Body */}
                <div className="AdminShell">
                    {/* Left nav */}
                    <aside className="AdminSidebar">
                        <div className="AdminSidebarTitle">Manager</div>
                        <nav className="AdminNav">
                            {managerMenuItems.map((item) => {
                                const isBackButton = item.to === "/";
                                return (
                                    <NavLink
                                        key={item.to}
                                        to={withLocale(item.to, globalState.Language)}
                                        className={navLinkClass}
                                        title={getMenuTooltip(item.code)}
                                        end={item.to === "/manager" || isBackButton}
                                    >
                                        {getMenuLabel(item.code, item.fallback)}
                                    </NavLink>
                                );
                            })}

                            {/* Uitloggen knop */}
                            <button
                                className="AdminNavLink AdminNavLink--button AdminNavLink--logout"
                                onClick={() => void handleLogout()}
                                title={getMenuTooltip("AcademyLogout")}
                            >
                                {getMenuLabel("AcademyLogout", "Uitloggen")}
                            </button>
                        </nav>
                    </aside>

                    {/* Main content */}
                    <main className="AdminMain">
                        <div className="AdminBarBreadcrumb">
                            <Link to={withLocale("/", globalState.Language)}>Academy</Link>
                            <span className="AdminBarBreadcrumbSep">/</span>
                            <span>Manager</span>
                        </div>
                        <Outlet />
                    </main>
                </div>

                {/* Footer (compact, manager context) */}
                <div className="AdminFooterSection">
                    <div className="AdminFooterContent">
                        <MalencoNaw />
                    </div>
                </div>
            </div>

            <div className="right"></div>
        </div>
    );
}
